import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { StoreState } from '../../reducers';
import { StaticPageIframe } from './styles';
import { Page } from '../Gallery/styles';
import RoutesUtils from '../../utils/routes';
import Utils from '../../utils/index';
import UserAgentUtils from '../../utils/userAgent';
import { AppInfosState, AppType } from '../App/reducer';
import { GalleryInfosState } from '../Gallery/reducer';
import { DriveItemListOrigin } from '../DriveItemListPage/constants';
import Header from '../MenuManager/Header';
import { RouterParamsState } from '../SetRouteParams/reducer';
import DriveItemListSearchField from '../DriveItemList/Search/Field';
import { DriveItemListSearchFieldDisplayType } from '../DriveItemList/Search/Field/constants';
import { DownloadManagerState } from '../DownloadManager/reducer';
import DownloadManagerUtils from '../../utils/downloadManager';
import { DriveListState } from '../DriveItemList/reducer';
import { DriveItemOrigin, viewDriveItem, getOrFetchDriveItemMeta, PublicationOptions } from '../DriveItemList/actions';
import { Api } from '../../typings/api';
import { AuthenticationState } from '../Authentication/reducer';
import AuthUtils from '../../utils/authentication';
import { PostMessageTarget, PostMessageAction } from '../../utils/iframeApi/ReaderApi';
import { createNotification } from '../NotificationBar/actions';

/*
// navitation can be triggered from the welcome page in the iframe

// Script to put anywhere in the welcomepage template
<script>
	function openLink(link) {
		parent.postMessage({
			target: 'GallApp',
			action: 'goTo',
			params: {
				url:link
			}
		}, '*')
	}
	function openDriveItem(id, page, hideWelcomePage) {
		parent.postMessage({
			target: 'GallApp',
			action: 'openDriveItem',
			params: {
				id:id,
				page:page,
				hideWelcomePage: hideWelcomePage
			}
		}, '*')
	}
</script>

// Exemple for a link that goes to "News" panel
<a href="javascript:openLink('/list/new')">a link </a> <br />

// Exemple for a link that goes to the folder 409327 and open the image 459052 in theater
<a href="javascript:openDriveItem('list/folder/409327/theater/459052')">a link </a> <br />


// Exemple that open image 472931 in the theater
<a href="javascript:openDriveItem(472931)">a link </a> <br />
*/

interface MessageGoto {
	target: PostMessageTarget.GallApp;
	action: PostMessageAction.GoTo;
	params: {
		url: string;
	};
}

interface MessageOpenDriveItem {
	target: PostMessageTarget.GallApp;
	action: PostMessageAction.OpenDriveItem;
	params: {
		id: number;
		page?: number;
		hideWelcomePage?: boolean;
	};
}

export interface StaticPageProps {
	appInfos: AppInfosState;
	galleryInfos: GalleryInfosState;
	routerParamsHistory: RouterParamsState[];
	downloadManager: DownloadManagerState;
	driveList: DriveListState;
	authentication: AuthenticationState;
	dispatch: Dispatch<StoreState>;
}

export class StaticPage extends React.Component<StaticPageProps, {}> {

	componentDidMount() {
		window.addEventListener('message', this.handleFrameTasks);
	}

	componentWillUnmount() {
		window.removeEventListener('message', this.handleFrameTasks);
	}

	handleFrameTasks = (e) => {
		if (e.data.target === 'GallApp') {
			if (e.data.action === 'goTo' && e.data.params && e.data.params.url) {
				this.handleMessageGoTo(e.data);
			}
			if (e.data.action === 'openDriveItem' && e.data.params && e.data.params.id) {
				this.handleMessageOpenDriveItem(e.data);
			}
		}
	}

	handleMessageGoTo(messageGoto: MessageGoto) {
		RoutesUtils.historyPush(messageGoto.params.url);
	}

	handleMessageOpenDriveItem(messageOpenDriveItem: MessageOpenDriveItem) {
		const { dispatch, driveList, downloadManager } = this.props;
		const { isMobileApp, isOnline } = UserAgentUtils;
		const isAsync = isMobileApp;

		const driveItemId = messageOpenDriveItem.params.id;
		const publicationOptions: PublicationOptions = {
			page: messageOpenDriveItem.params.page,
			hideWelcomePage: messageOpenDriveItem.params.hideWelcomePage,
		};
		const driveItemMeta = driveList.driveItems[driveItemId];
		const needFetching = !driveItemMeta && isOnline();
		const isDownloaded = DownloadManagerUtils.isItemDownloaded(downloadManager, driveItemId);
		const isAccessible = driveItemMeta && (isOnline() || isDownloaded || driveItemMeta.type === Api.DriveItemType.Directory || driveItemMeta.type === Api.DriveItemType.Image);

		if (needFetching) {
			dispatch(getOrFetchDriveItemMeta(driveItemId, false, isAsync))
				.then((driveItem: Api.DriveItem) => {
					dispatch(viewDriveItem(driveItem, DriveItemOrigin.Menu, publicationOptions));
				});
		} else if (isAccessible) {
			dispatch(viewDriveItem(driveItemMeta, DriveItemOrigin.Menu, publicationOptions));
		} else {
			dispatch(createNotification('info.offline_download_single_asset'));
			return;
		}
	}

	resolveComponentId(component: string | undefined): number {
		const { galleryInfos, driveList } = this.props;
		const { welcomepage } = galleryInfos.template;

		if (!component && welcomepage) {
			return Utils.getMediaId(welcomepage.url);
		} else if (component && driveList.driveItems[component]) {
			return parseInt(component, 10);
		} else {
			RoutesUtils.getPageNotFound();
			return 0;
		}
	}

	getIframeUrl(): string {
		const { galleryInfos, routerParamsHistory, downloadManager, driveList, authentication, appInfos } = this.props;
		const { template } = galleryInfos;
		const { needCredentials, mode } = authentication.userInfos;
		const welcomePage = template && template.welcomepage;
		const appType = appInfos.type;
		const { component } = routerParamsHistory[routerParamsHistory.length - 1].params;
		const isWelcomePagePath = !component;
		const needConnection = needCredentials && AuthUtils.isNotConnected(mode) && !galleryInfos.isRemoteTemplateLoaded;

		if (!galleryInfos.isConfigLoaded
			|| !(appType === AppType.Single ? galleryInfos.isLocalTemplateLoaded : galleryInfos.isRemoteTemplateLoaded)
			|| !galleryInfos.template.menu || (component && !driveList.driveItems[component])
			|| needConnection
		) {
			return '';
		}

		if (isWelcomePagePath && (!welcomePage || !welcomePage.url)) {
			RoutesUtils.historyPush(RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Folder, null));
			return '';
		}

		const componentId = this.resolveComponentId(component);
		const isAvailableOffline = DownloadManagerUtils.isItemDownloaded(downloadManager, componentId);
		const componentUrl = Utils.getComponentUrl(galleryInfos, componentId, isAvailableOffline, isWelcomePagePath);

		// adding the / at the end is necessary for now for galleries viewed in https
		// Without it, on the french production server, there is a redirection from to an url using http protocol
		const iframeSrcUrl = componentUrl.endsWith('/index.html') ? componentUrl : `${componentUrl}/`;

		return iframeSrcUrl;
	}

	render() {
		const { routerParamsHistory } = this.props;
		const { origin, originParam } = routerParamsHistory[routerParamsHistory.length - 1].params;

		return (
			<div>
				<Header />
				<Page>
					<DriveItemListSearchField
						searchText={origin === DriveItemListOrigin.Search ? originParam : ''}
						displayType={DriveItemListSearchFieldDisplayType.Mobile}
					/>
					<StaticPageIframe src={this.getIframeUrl()} />
				</Page>
			</div>
		);
	}
}

const mapStateToProps = (state: StoreState) => {
	const { appInfos, galleryInfos, routerParamsHistory, downloadManager, driveList, authentication } = state;
	return { appInfos, galleryInfos, routerParamsHistory, downloadManager, driveList, authentication };
};

export default connect(mapStateToProps)(StaticPage);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/StaticPage/index.tsx